import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  Alert,
  ScrollView,
} from 'react-native';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import {
  Appbar,
  Searchbar,
  FAB,
  Portal,
  Modal as PaperModal,
  Button,
  Text,
  ActivityIndicator,
  Chip,
  Card,
  Title,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import apiService, { 
  Cocktail, 
  CocktailFilters, 
  CocktailStats 
} from '../services/ApiService';
import AdvancedCocktailFilters from '../components/AdvancedCocktailFilters';
import EnhancedCocktailCard from '../components/EnhancedCocktailCard';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedContainer, ThemedText } from '../components/ThemedContainer';

const CocktailsScreen: React.FC = () => {
  const { themeData } = useTheme();
  const navigation = useNavigation();
  const [cocktails, setCocktails] = useState<Cocktail[]>([]);
  const [filteredCocktails, setFilteredCocktails] = useState<Cocktail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<CocktailFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const [stats, setStats] = useState<CocktailStats | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load cocktails on mount
  useEffect(() => {
    loadCocktails();
    loadStats();
  }, []);

  // Handle navigation with search terms
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      const params = (navigation as any).getState()?.routes?.find((route: any) => route.name === 'Cocktails')?.params;
      console.log('🧭 CocktailsScreen focus - params:', params);
      if (params?.searchTerm) {
        console.log('🧭 Setting search query to:', params.searchTerm);
        setSearchQuery(params.searchTerm);
        // Clear the search term from navigation params to prevent re-triggering
        (navigation as any).setParams({ searchTerm: undefined });
      }
    });

    return unsubscribe;
  }, [navigation]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filters, searchQuery, cocktails]);

  const loadCocktails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading cocktails...');
      const data = await apiService.getCocktails();
      console.log('✅ Cocktails loaded:', data.length, 'cocktails');
      console.log('📝 First cocktail:', data[0]?.name);
      setCocktails(data);
      setFilteredCocktails(data); // Initialize filtered cocktails with all data
    } catch (error) {
      console.error('Error loading cocktails:', error);
      Alert.alert('Error', 'Failed to load cocktails');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await apiService.getDatabaseStats();
      console.log('📊 Raw stats data:', data);
      // Map the backend response to the expected CocktailStats interface
      const safeStats = {
        total_cocktails: Number(data.cocktails || 0), // Map 'cocktails' to 'total_cocktails'
        families: Number(data.families || 0),
        methods: Number(data.methods || 0),
        glasses: Number(data.glasses || 0),
        avg_abv: data.avg_abv ? Number(data.avg_abv) : null,
        avg_difficulty: data.avg_difficulty ? Number(data.avg_difficulty) : null,
        avg_prep_time: data.avg_prep_time ? Number(data.avg_prep_time) : null,
        low_strength_count: Number(data.low_strength_count || 0),
        standard_strength_count: Number(data.standard_strength_count || 0),
        strong_strength_count: Number(data.strong_strength_count || 0),
      };
      console.log('📊 Mapped stats:', safeStats);
      setStats(safeStats);
    } catch (error) {
      console.error('Error loading stats:', error);
      // Set default stats to prevent crashes
      setStats({
        total_cocktails: 0,
        families: 0,
        methods: 0,
        glasses: 0,
        avg_abv: null,
        avg_difficulty: null,
        avg_prep_time: null,
        low_strength_count: 0,
        standard_strength_count: 0,
        strong_strength_count: 0,
      });
    }
  };

  const applyFilters = () => {
    try {
      let filtered = [...cocktails];
      console.log('🔍 Applying filters to', filtered.length, 'cocktails');

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(cocktail => {
          // Parse ingredients if it's a string
          let ingredients = [];
          try {
            if (typeof cocktail.ingredients === 'string') {
              ingredients = JSON.parse(cocktail.ingredients);
            } else if (Array.isArray(cocktail.ingredients)) {
              ingredients = cocktail.ingredients;
            }
          } catch (e) {
            console.warn('Failed to parse ingredients for search:', e);
          }
          
          return cocktail.name.toLowerCase().includes(query) ||
            cocktail.instructions?.toLowerCase().includes(query) ||
            cocktail.family?.toLowerCase().includes(query) ||
            cocktail.method?.toLowerCase().includes(query) ||
            cocktail.glass?.toLowerCase().includes(query) ||
            ingredients.some((ing: any) => ing.name.toLowerCase().includes(query));
        });
      }

      // Apply family filter
      if (filters.family) {
        filtered = filtered.filter(cocktail => 
          cocktail.family?.toLowerCase().includes(filters.family!.toLowerCase())
        );
      }

      // Apply method filter
      if (filters.method) {
        filtered = filtered.filter(cocktail => 
          cocktail.method?.toLowerCase() === filters.method!.toLowerCase()
        );
      }

      // Apply glassware filter
      if (filters.glassware) {
        filtered = filtered.filter(cocktail => 
          cocktail.glass?.toLowerCase().includes(filters.glassware!.toLowerCase())
        );
      }

      // Apply difficulty filter
      if (filters.difficulty_max) {
        filtered = filtered.filter(cocktail => 
          cocktail.difficulty && cocktail.difficulty <= filters.difficulty_max!
        );
      }

      // Apply prep time filter
      if (filters.prep_time_max) {
        filtered = filtered.filter(cocktail => 
          cocktail.prep_time_seconds && cocktail.prep_time_seconds <= filters.prep_time_max!
        );
      }

      console.log('✅ Filtered to', filtered.length, 'cocktails');
      setFilteredCocktails(filtered);
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const handleCocktailPress = async (cocktail: Cocktail) => {
    try {
      // Navigate to the detail screen instead of showing modal
      (navigation as any).navigate('CocktailDetail', { cocktailId: cocktail.cocktail_id });
    } catch (error) {
      console.error('Error navigating to cocktail detail:', error);
      Alert.alert('Error', 'Failed to open cocktail details');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadCocktails(), loadStats()]);
    setRefreshing(false);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          count += value.length;
        } else {
          count += 1;
        }
      }
    });
    if (searchQuery) count += 1;
    return count;
  };

  const renderStats = () => {
    if (!stats) return null;

    return (
      <Card style={styles.statsCard}>
        <Card.Content>
          <Title style={styles.statsTitle}>Cocktail Database</Title>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{stats.total_cocktails}</Text>
              <Text style={styles.statLabel}>Total Cocktails</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{stats.families}</Text>
              <Text style={styles.statLabel}>Families</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{stats.methods}</Text>
              <Text style={styles.statLabel}>Methods</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{stats.glasses}</Text>
              <Text style={styles.statLabel}>Glassware</Text>
            </View>
          </View>
          <View style={styles.statsRow}>
            <Text style={styles.statText}>
              Avg ABV: {stats.avg_abv !== null ? `${stats.avg_abv.toFixed(1)}%` : 'N/A'}
            </Text>
            <Text style={styles.statText}>
              Avg Difficulty: {stats.avg_difficulty !== null ? `${stats.avg_difficulty.toFixed(1)}/5` : 'N/A'}
            </Text>
          </View>
        </Card.Content>
      </Card>
    );
  };

  const renderFilterChips = () => {
    const activeFilters: string[] = [];
    
    if (searchQuery) activeFilters.push(`Search: "${searchQuery}"`);
    if (filters.strength_band) activeFilters.push(`Strength: ${filters.strength_band}`);
    if (filters.base_spirit) activeFilters.push(`Base: ${filters.base_spirit}`);
    if (filters.method) activeFilters.push(`Method: ${filters.method}`);
    if (filters.glassware) activeFilters.push(`Glass: ${filters.glassware}`);
    if (filters.difficulty_max) activeFilters.push(`Max Difficulty: ${filters.difficulty_max}`);
    if (filters.prep_time_max) activeFilters.push(`Max Time: ${apiService.formatPrepTime(filters.prep_time_max)}`);
    if (filters.flavor_tags?.length) activeFilters.push(`${filters.flavor_tags.length} flavor tags`);
    if (filters.occasion_tags?.length) activeFilters.push(`${filters.occasion_tags.length} occasion tags`);
    if (filters.dietary_flags?.length) activeFilters.push(`${filters.dietary_flags.length} dietary flags`);

    if (activeFilters.length === 0) return null;

    return (
      <View style={styles.filterChipsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {activeFilters.map((filter, index) => (
            <Chip key={index} style={styles.filterChip} mode="outlined">
              {filter}
            </Chip>
          ))}
        </ScrollView>
        <Button mode="text" onPress={clearFilters} compact>
          Clear All
        </Button>
      </View>
    );
  };

  const handleSearchTerm = (term: string) => {
    setSearchQuery(term);
  };

  const renderCocktailItem = ({ item }: { item: Cocktail }) => {
    console.log('🎯 Rendering cocktail item:', item.name);
    return (
      <EnhancedCocktailCard
        cocktail={item}
        onPress={handleCocktailPress}
        showDetails={false}
        onSearchTerm={handleSearchTerm}
      />
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Ionicons name="wine-outline" size={64} color={themeData.colors.primary} />
      <Text style={styles.emptyStateTitle}>No cocktails found</Text>
      <Text style={styles.emptyStateText}>
        Try adjusting your search or filters to find what you're looking for.
      </Text>
      <Button mode="contained" onPress={clearFilters} style={styles.clearButton}>
        Clear Filters
      </Button>
    </View>
  );

  console.log('🎨 Rendering CocktailsScreen:', {
    loading,
    cocktailsCount: cocktails.length,
    filteredCount: filteredCocktails.length,
    stats: stats?.total_cocktails
  });

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={themeData.colors.primary} />
        <Text style={styles.loadingText}>Loading cocktails...</Text>
      </View>
    );
  }

  return (
    <ThemedContainer style={styles.container}>
      {/* Header */}
      <Appbar.Header>
        <Appbar.Content title="Cocktails" subtitle={`${filteredCocktails.length} cocktails`} />
        <Appbar.Action 
          icon="filter-variant" 
          onPress={() => setShowFilters(true)}
        />
      </Appbar.Header>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder="Search cocktails..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={[styles.searchbar, { backgroundColor: themeData.colors.cardBackground }]}
          inputStyle={{ color: themeData.colors.text }}
        />
      </View>

      {/* Compact Stats Card */}
      <Card style={[styles.statsCard, { backgroundColor: themeData.colors.cardBackgroundVariant }]}>
        <Card.Content style={styles.statsContent}>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight1 }]}>
                {stats?.total_cocktails || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Cocktails
              </Text>
            </View>
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight2 }]}>
                {stats?.families || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Families
              </Text>
            </View>
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight3 }]}>
                {stats?.methods || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Methods
              </Text>
            </View>
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight4 }]}>
                {stats?.glasses || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Glasses
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Filter Chips */}
      {renderFilterChips()}

      {/* Cocktails List */}
      <FlatList
        data={filteredCocktails}
        renderItem={renderCocktailItem}
        keyExtractor={(item) => item.cocktail_id.toString()}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        ListEmptyComponent={renderEmptyState}
        ListHeaderComponent={() => (
          <Text style={{ padding: 16, color: themeData.colors.textSecondary }}>
            Showing {filteredCocktails.length} of {stats?.total_cocktails || 0} cocktails
          </Text>
        )}
        onLayout={() => console.log('📱 FlatList onLayout - data length:', filteredCocktails.length)}
        onContentSizeChange={() => console.log('📏 FlatList onContentSizeChange')}
      />

      {/* Filters Modal */}
      <Portal>
        <PaperModal
          visible={showFilters}
          onDismiss={() => setShowFilters(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Advanced Filters</Text>
            <Button onPress={() => setShowFilters(false)}>Done</Button>
          </View>
          <AdvancedCocktailFilters
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={clearFilters}
          />
        </PaperModal>
      </Portal>

      
    </ThemedContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  searchContainer: {
    padding: 12,
    paddingBottom: 8,
  },
  searchbar: {
    elevation: 2,
  },
  statsCard: {
    margin: 16,
    marginBottom: 8,
    elevation: 2,
  },
  statsContent: {
    padding: 12,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 11,
    marginTop: 2,
  },

  filterChipsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  filterChip: {
    marginRight: 8,
  },
  listContainer: {
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  clearButton: {
    marginTop: 8,
  },
  modalContainer: {
    margin: 20,
    borderRadius: 12,
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  detailContainer: {
    flex: 1,
  },
});

export default CocktailsScreen; 